import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Warehouse, Trash2, Plus, ShieldCheck } from 'lucide-react';
import { xamplinApi } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { toast } from '@/components/ui/Toast';
import { usePagination } from '@/lib/hooks/usePagination';
import { Pagination } from '@/components/ui/Pagination';
import { HelpHint } from '@/features/help/HelpHint';

/**
 * Operations settings (build-v2 G13) — warehouses (real CRUD, agency-scoped)
 * and asset protocols (reference checklist), mirroring the prototype
 * OperationsWarehouses / OperationsProtocols panes.
 */
const PROTOCOLS = [
  { name: 'Setup verification', desc: 'Foto + checklist por asset desplegado (RS-1)' },
  { name: 'Condition report', desc: 'New / used / damaged al cierre del evento' },
  { name: 'Return-to-warehouse', desc: 'Reconciliación de stock distribuido vs sobrante' },
  { name: 'Damage escalation', desc: 'Foto + nota → manager; baja del inventario' },
];

export function OperationsPage() {
  const qc = useQueryClient();
  const whQ = useQuery({ queryKey: queryKeys.xamplin.warehouses, queryFn: () => xamplinApi.warehouses.list() });
  const warehouses = whQ.data ?? [];
  const pg = usePagination(warehouses, 12);

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState('');

  const createM = useMutation({
    mutationFn: () =>
      xamplinApi.warehouses.create({
        name: name.trim(),
        location: location.trim() || undefined,
        capacity: capacity ? Number(capacity) : undefined,
      }),
    onSuccess: () => {
      setName('');
      setLocation('');
      setCapacity('');
      toast({ tone: 'success', title: 'Almacén creado' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.warehouses });
    },
    onError: () => toast({ tone: 'error', title: 'No se pudo crear' }),
  });
  const removeM = useMutation({
    mutationFn: (id: number) => xamplinApi.warehouses.remove(id),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Almacén eliminado' });
      void qc.invalidateQueries({ queryKey: queryKeys.xamplin.warehouses });
    },
  });

  return (
    <div className="page flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="grid h-8 w-8 place-items-center rounded-lg border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
            Operations
          </h1>
          <p className="mono text-xs uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            Warehouses · asset protocols
          </p>
        </div>
        <div className="ml-auto">
          <HelpHint section="operations" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Warehouses */}
        <section className="flex flex-col gap-3">
          <h2 className="mono flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            <Warehouse className="h-3.5 w-3.5" /> Warehouses
          </h2>
          {whQ.isLoading ? (
            <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando…</div>
          ) : (
            <div className="flex flex-col gap-2">
              {pg.pageItems.map((w) => (
                <div key={w.id} className="flex items-center gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[var(--color-xk-lavender)]">{w.name}</div>
                    <div className="mono text-[10px] text-[var(--color-xk-text-muted)]">
                      {w.location || '—'}{w.capacity ? ` · cap ${w.capacity.toLocaleString()}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeM.mutate(w.id)}
                    className="grid h-7 w-7 place-items-center rounded-lg text-[var(--color-xk-text-subtle)] hover:text-[var(--color-error)]"
                    aria-label="Eliminar"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {warehouses.length === 0 && (
                <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">Sin almacenes aún.</span>
              )}
              <Pagination {...pg} />
            </div>
          )}

          {/* Create */}
          <div className="flex flex-col gap-2 rounded-xl border border-dashed border-[var(--color-xk-border)] p-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del almacén" className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-1.5 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]" />
            <div className="flex gap-2">
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ubicación" className="min-w-0 flex-1 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-1.5 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]" />
              <input value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="Cap." type="number" className="w-24 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-1.5 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]" />
            </div>
            <button
              type="button"
              disabled={!name.trim() || createM.isPending}
              onClick={() => createM.mutate()}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
            >
              <Plus className="h-4 w-4" /> Añadir almacén
            </button>
          </div>
        </section>

        {/* Asset protocols */}
        <section className="flex flex-col gap-3">
          <h2 className="mono flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
            <ShieldCheck className="h-3.5 w-3.5" /> Asset protocols
          </h2>
          <div className="flex flex-col gap-2">
            {PROTOCOLS.map((p) => (
              <div key={p.name} className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
                <div className="text-sm font-semibold text-[var(--color-xk-lavender)]">{p.name}</div>
                <div className="mono text-[10px] text-[var(--color-xk-text-muted)]">{p.desc}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
